import React, { useState } from "react";
import { QuestionResponse } from "@/entities/support/api/questions-api";
import { useUpdateQuestion } from "@/features/profile";
import { updateQuestionUseCase } from "@/features/profile/lib/update-question.use-case";

interface MyQuestionsDetailProps {
  question: QuestionResponse;
  onBack: () => void;
  onUpdateSuccess?: () => void;
}

/**
 * 문의 상세보기 및 수정 컴포넌트
 */
export const MyQuestionsDetail = ({
  question,
  onBack,
  onUpdateSuccess,
}: MyQuestionsDetailProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editTitle, setEditTitle] = useState(question.title);
  const [editContent, setEditContent] = useState(question.content);
  const [validationError, setValidationError] = useState("");
  const updateMutation = useUpdateQuestion(question.id);

  const handleSaveEdit = async () => {
    setValidationError("");

    try {
      // Use-case에서 입력 검증
      updateQuestionUseCase.validateInput({
        title: editTitle,
        content: editContent,
        category: question.category,
      });

      // API 호출
      await updateMutation.mutateAsync({
        title: editTitle,
        content: editContent,
        category: question.category,
      });

      setIsModalOpen(false);
      onUpdateSuccess?.(); // 업데이트 성공 후 콜백 호출
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "수정에 실패했습니다";
      setValidationError(errorMessage);
      console.error("Update failed:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditTitle(question.title);
    setEditContent(question.content);
    setIsModalOpen(false);
  };

  return (
    <div>
      {/* 뒤로가기 버튼 */}
      {/* <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-700 hover:text-gray-900 mb-8 font-medium"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
        <span>목록으로</span>
      </button> */}

      {/* 문의 내용 섹션 */}
      <div className="mb-12">
        <h2 className="text-lg font-normal text-black mb-6">문의 내용</h2>

        <div className="border border-gray-300">
          {/* 정보 행: 이름, 문의 유형, 이메일, 전화번호 */}
          <div className="border-b border-gray-300 bg-white">
            <div className="flex h-11">
              <div className="w-16 h-11 bg-gray-100 border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-gray-600 font-normal">이름</span>
              </div>
              <div className="flex-1 flex items-center px-4">
                <span className="text-sm text-gray-900">
                  {question.author?.name || "-"}
                </span>
              </div>

              <div className="w-20 h-11 bg-gray-100 border-l border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-gray-600 font-normal">
                  문의 유형
                </span>
              </div>
              <div className="flex-1 flex items-center px-4">
                <span className="text-sm text-gray-900">
                  {question.category}
                </span>
              </div>

              <div className="w-20 h-11 bg-gray-100 border-l border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-gray-600 font-normal">이메일</span>
              </div>
              <div className="flex-1 flex items-center px-4">
                <span className="text-sm text-gray-900">
                  {question.email || question.author?.email || "-"}
                </span>
              </div>

              <div className="w-20 h-11 bg-gray-100 border-l border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-gray-600 font-normal">
                  전화 번호
                </span>
              </div>
              <div className="flex-1 flex items-center px-4">
                <span className="text-sm text-gray-900">
                  {question.phone_number || "-"}
                </span>
              </div>
            </div>
          </div>

          {/* 제목 행 */}
          <div className="border-b border-gray-300 bg-white">
            <div className="flex h-11">
              <div className="w-16 h-11 bg-gray-100 border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-gray-600 font-normal">제목</span>
              </div>
              <div className="flex-1 flex items-center px-4">
                <span className="text-sm text-gray-900">{question.title}</span>
              </div>
            </div>
          </div>

          {/* 내용 행 */}
          <div className="border-b border-gray-300 bg-white">
            <div className="flex">
              <div className="w-16 bg-gray-100 border-r border-gray-300 flex items-start justify-center flex-shrink-0 p-4">
                <span className="text-sm text-gray-600 font-normal">내용</span>
              </div>
              <div className="flex-1 p-4 min-h-32">
                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                  {question.content}
                </p>
              </div>
            </div>
          </div>

          {/* 공개 여부 행 (읽기 전용) */}
          <div className="bg-white h-11">
            <div className="flex items-center h-11">
              <div className="w-24 h-11 bg-gray-100 border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-gray-600 font-normal">
                  공개 여부
                </span>
              </div>
              <div className="flex-1 flex items-center px-4">
                <span className="text-sm text-gray-900">
                  {question.is_public ? "공개" : "비공개"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 문의 답변 섹션 */}
      {question.comment && (
        <div>
          <h2 className="text-lg font-normal text-black mb-6">문의 답변</h2>

          <div className="border border-gray-300">
            {/* 헤더 */}
            <div className="border-b border-gray-300 bg-white">
              <div className="flex h-11">
                <div className="w-20 h-11 bg-gray-100 border-l border-r border-gray-300 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm text-gray-600 font-normal">
                    처리 상태
                  </span>
                </div>
                <div className="w-32 flex items-center px-4">
                  <span className="text-sm text-gray-500">답변 완료</span>
                </div>
              </div>
            </div>

            {/* 답변 내용 행 */}
            <div className="bg-white">
              <div className="flex">
                <div className="w-16 bg-gray-100 border-r border-gray-300 flex items-start justify-center flex-shrink-0 p-4">
                  <span className="text-sm text-gray-600 font-normal">내용</span>
                </div>
                <div className="flex-1 p-4 min-h-52">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">
                    {question.comment}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 버튼 그룹 */}
      <div className="flex gap-3 justify-end mt-8 pt-6 border-t border-gray-200">
        {!question.comment && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-8 py-3 bg-orange-600 text-white rounded hover:bg-orange-700 font-medium"
          >
            수정
          </button>
        )}
        <button
          onClick={onBack}
          className="px-8 py-3 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium"
        >
          목록으로
        </button>
      </div>

      {/* 수정 모달 */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-6">문의 수정</h2>

            {/* 검증 오류 메시지 */}
            {validationError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                {validationError}
              </div>
            )}

            <div className="space-y-4 mb-6">
              {/* 제목 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  제목 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => {
                    setEditTitle(e.target.value);
                    setValidationError("");
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  placeholder="제목"
                />
              </div>

              {/* 내용 */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  내용 <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={editContent}
                  onChange={(e) => {
                    setEditContent(e.target.value);
                    setValidationError("");
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none h-32"
                  placeholder="내용"
                />
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCancelEdit}
                className="px-6 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50 font-medium"
              >
                취소
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={updateMutation.isPending}
                className="px-6 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 font-medium disabled:bg-gray-400"
              >
                {updateMutation.isPending ? "저장 중..." : "수정하기"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
